import React, { useState, useEffect } from "react";
import useTheme from "../../../../hooks/useTheme";

import { Icon, Image } from "semantic-ui-react";
import { AppColumn, AppRow } from "../../../UI/Layouts";
import AppText from "../../../UI/Texts/AppTexts";
import { SizedBox } from "../../../UI/Sizes";
import { useResponsive } from "../../../UI/ResponsiveContext";
import { useSelector } from "react-redux";
import {
  downloadFile,
  encodeBase64URL,
  getLocale,
  getMoney,
  isoDate,
  withOpacity,
} from "../../../../utils/helperFunctions";
import { t } from "i18next";
import {
  CandidateStatusCard,
  CandidateStatusVerticalStepper,
} from "../../../UI/CandidateCardStatus";
import AppliedJobActions from "./JobCandidateDetailActions";
import EventDetailSection from "./CandidateEventDetail";
import { JobCandidateFormSource } from "../../../../utils/Constants/enums";
import {
  getJobApplicationProcessSteps,
  getNextIncompleteApplicationStep,
} from "../../../../utils/Constants/jobApplicationProcessSteps";
import HirePossibility from "../../ApplyCandidate/Components/HirePossibility";
import { AppLinkedText, AppPrimaryButton } from "../../../UI/CustomButtons";
import { useNavigate } from "react-router-dom";
import { getDocumentNameType } from "../../../../utils/Constants/documentIDs";
import CircularLoader from "../../../UI/LoadingIndicators/CircularLoadingIndicators";
import axios from "axios";
import {
  DELETE_CANDIDATE_DOCUMENTS,
  FETCH_APPLICATION_DOCUMENTS,
} from "../../../../utils/Controller/URLConstants";
import { getFeeMatrixForWorkplace } from "../../../../utils/feeFunctions";
import {
  getApplicationDetailProgressSteps,
  navigateToApplicationStep,
} from "./CandidateDetailMainContent.logic";

const CandidateDetailMainContent = ({
  currentUser,
  profile,
  candidateData,
  jobData,
  coreConstants,
  eventDetailData,
  onClose,
  showToast,
  onCandidateChange,
}) => {
    const theme = useTheme();
  const navigate = useNavigate();

  const { isMobile } = useResponsive();
  const feeConfigs = useSelector((state) => state.core.feeConfigs) ?? {};

  const [isDeletedDocumentData, setIsDeletedDocument] = useState({});
  const [documents, setDocuments] = useState({});
  const [, setIsLoadingDocuments] = useState(true);

  const { jobDetails } = candidateData;
  const applicationJobId =
    candidateData?.jobDetails?.jobId ?? candidateData?.jobId ?? jobData?.jobId;

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!candidateData?.id) {
        setIsLoadingDocuments(false);
        return;
      }

      try {
        const response = await axios.get(FETCH_APPLICATION_DOCUMENTS, {
          params: {
            candidateID: candidateData.id,
            email: currentUser?.email ?? candidateData?.candidate?.email ?? candidateData?.email,
          },
        });

        if (response.data?.status === 0) {
          setDocuments(response.data.documents ?? {});
        }
      } catch (error) {
        console.error("Error fetching application documents:", error);
      } finally {
        setIsLoadingDocuments(false);
      }
    };

    fetchDocuments();
  }, [
    candidateData?.id,
    candidateData?.candidate?.email,
    candidateData?.email,
    currentUser?.email,
  ]);

  const userDocuments = Object.values(documents ?? {}).filter(
    (document) =>
      document.createdBy === currentUser?.uid || !document.createdBy,
  );

  const { baseFee, profession, healthcareWorkplaceDetails, workplaceDetails } =
    jobData;

  const { text: compansationFee, canDisplayCompensation } =
    getFeeMatrixForWorkplace({
      baseJobFeeAmount: baseFee,
      workplaceId: jobData?.workplaceDetails?.workPlaceId,
      feeConfigs: feeConfigs,
    });

  const actualWorkplaceDetails = healthcareWorkplaceDetails ?? workplaceDetails;

  const jobCity =
    healthcareWorkplaceDetails?.city &&
    healthcareWorkplaceDetails.city.length > 0
      ? healthcareWorkplaceDetails.city
      : healthcareWorkplaceDetails?.municipality;

  const professionTranslation = (() => {
    const roles = coreConstants.workroles ?? [];

    if (roles.length > 0) {
      return (
        roles.find((e) => e.id === profession)?.text[getLocale()] ?? profession
      );
    }

    return profession;
  })();

  const logoUrl = (() => {
    if (
      healthcareWorkplaceDetails?.logoUrl &&
      healthcareWorkplaceDetails?.logoUrl.length > 0
    ) {
      return healthcareWorkplaceDetails?.logoUrl;
    }
    return workplaceDetails?.logoUrl;
  })();

  const onDeleteCandidateDocument = async (fileData, onSuccess) => {
    const documentId = fileData?.id;

    try {
      if (documentId) {
        const deleteData = {
          documentId: documentId,
          jobId: applicationJobId,
          email: currentUser?.email ?? candidateData?.candidate?.email ?? candidateData?.email,
          candidateID: candidateData.id,
        };

        setIsDeletedDocument((prevState) => ({
          ...prevState,
          [documentId]: true,
        }));

        const response = await axios.post(
          DELETE_CANDIDATE_DOCUMENTS,
          deleteData,
        );

        if (response.data?.status === 0) {
          // Remove deleted document from local state
          setDocuments((prevDocs) => {
            const newDocs = { ...prevDocs };
            delete newDocs[documentId];
            return newDocs;
          });

          const updatedCandidateData = response.data?.candidateData;

          if (updatedCandidateData) {
            if (onSuccess) {
              onSuccess(updatedCandidateData);
            }
          }
        }

        setIsDeletedDocument((prevState) => ({
          ...prevState,
          [documentId]: false,
        }));
      }
    } catch (error) {
      console.error("Error deleting candidate document:", error);

      setIsDeletedDocument((prevState) => ({
        ...prevState,
        [documentId]: false,
      }));
    }
  };

  const encodedString = encodeBase64URL(
    JSON.stringify({
      job: applicationJobId,
      uid:
        candidateData?.contactPerson?.uid ??
        candidateData?.contacts?.uid ??
        candidateData?.contacts?.uids?.[0],
      purpose: "job_candidate_form",
    }),
  );

  const searchParams = new URLSearchParams(
    `?preview=${encodedString}&source=${JobCandidateFormSource.internal}&page=application-summary`,
  );

  const applicationStepsPages = getJobApplicationProcessSteps({
    searchParams: searchParams,
    candidateData: candidateData,
    profile: profile,
  });

  const pagesForSummary = getApplicationDetailProgressSteps({
    applicationSteps: applicationStepsPages,
    jobDetails,
  });

  const currentCompletedSteps = pagesForSummary.filter(
    (page) => page.completed,
  );

  const unCompletedSteps = pagesForSummary.filter((page) => !page.completed);
  const nextPage = getNextIncompleteApplicationStep(pagesForSummary);

  return (
    <div
      style={{ flex: "1", overflowY: "auto", padding: theme.spacing.contentElementPadding }}
    >
      <AppColumn>
        <AppRow
          mainAxisAlignment={"space-between"}
          crossAxisAlignment={"start"}
        >
          <AppRow>
            {logoUrl?.length > 0 ? (
              <Image
                src={logoUrl}
                alt={actualWorkplaceDetails?.name}
                loading="lazy"
                style={{
                  height: isMobile ? "60px" : "80px",
                  width: isMobile ? "60px" : "80px",
                  borderRadius: theme.radius.default,
                  aspectRatio: "1",
                  objectFit: "cover",
                  backgroundColor: theme.colors.lightGrey,
                  border: `0.5px solid grey`,
                }}
              />
            ) : (
              <AppColumn
                crossAxisAlignment={"center"}
                mainAxisAlignment={"center"}
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  height: isMobile ? "60px" : "80px",
                  width: isMobile ? "60px" : "80px",
                  borderRadius: theme.radius.default,
                  border: `0.5px solid grey`,
                }}
              >
                <Icon
                  name="first aid"
                  style={{
                    fontSize: "3rem",
                    color: theme.primaryColor,
                    aspectRatio: "1",
                    objectFit: "cover",
                    marginLeft: "0.2rem",
                    marginBottom: "0.2rem",
                  }}
                />
              </AppColumn>
            )}
            <SizedBox width={theme.spacing.elementPadding} />
            <AppColumn>
              {!isMobile && (
                <AppColumn>
                  <AppText.Footnote color={theme.colors.textPrimary}>
                    {t("application_for")}
                  </AppText.Footnote>
                  <SizedBox height={"0.25rem"} />
                </AppColumn>
              )}

              <AppText.SubHeading
                fontWeight={600}
                spans={[
                  {
                    text: jobCity && `${jobCity}`,
                    className: "text-callout",
                    style: {
                      color: theme.colors.dark,
                      paddingLeft: theme.spacing.elementPaddingSmall,
                    },
                  },
                ]}
              >{`${professionTranslation}, `}</AppText.SubHeading>

              <SizedBox height={"0.25rem"} />

              <AppText.Caption margins={{ marginTop: "0px" }}>
                {`${actualWorkplaceDetails?.name}`}
              </AppText.Caption>

              {candidateData?.tenantName && (
                <AppText.Footnote color={theme.colors.grey}>
                  {`${t("via")} ${candidateData.tenantName}`}
                </AppText.Footnote>
              )}

              {(candidateData?.contactPerson?.firstName ||
                candidateData?.contactPerson?.lastName) && (
                <AppRow crossAxisAlignment={"center"} style={{ marginTop: "4px" }}>
                  {candidateData.contactPerson.profileImageUrl && (
                    <Image
                      src={candidateData.contactPerson.profileImageUrl}
                      avatar
                      style={{
                        width: "24px",
                        height: "24px",
                        marginRight: "6px",
                      }}
                    />
                  )}
                  <AppText.Footnote color={theme.colors.grey}>
                    {`${t("contact")}: ${candidateData.contactPerson.firstName ?? ""} ${candidateData.contactPerson.lastName ?? ""}`.trim()}
                  </AppText.Footnote>
                </AppRow>
              )}
            </AppColumn>
          </AppRow>
          {!isMobile && (
            <AppColumn>
              {canDisplayCompensation && (
                <AppText.SubHeading
                  color={theme.primaryColor}
                  fontWeight={600}
                  spans={[
                    {
                      text: ` |timme`,
                      className: "text-body",
                      style: {
                        color: theme.colors.dark,
                      },
                    },
                  ]}
                >
                  {getMoney(compansationFee)}
                </AppText.SubHeading>
              )}
              <SizedBox height={theme.spacing.elementPadding} />
              <AppRow mainAxisAlignment={"end"}>
                <CandidateStatusCard candidateData={candidateData} />
              </AppRow>
            </AppColumn>
          )}
        </AppRow>

        <SizedBox height={theme.spacing.contentElementPadding} />
        <AppText.Caption fontWeight={600}>
          {t("application_status")}
        </AppText.Caption>
        <CandidateStatusVerticalStepper
          candidateData={candidateData}
          onSubmited={onClose}
        />

        <SizedBox height={theme.spacing.elementPadding} />

        <AppliedJobActions
          candidateData={candidateData}
          currentUser={currentUser}
          onSubmited={(jobCandidate, documents) => {
            if (onCandidateChange) onCandidateChange(jobCandidate);
          }}
          showToast={showToast}
        />

        <SizedBox height={theme.spacing.contentPadding} />
        <div
          style={{
            padding: theme.spacing.elementPadding,
            backgroundColor: withOpacity(theme.colors.grey, 0.05),
            borderRadius: theme.radius.default,
            border: `1px solid ${withOpacity(theme.colors.grey, 0.2)}`,
          }}
        >
          <HirePossibility mainPages={pagesForSummary} />
          <SizedBox height={theme.spacing.contentElementPadding} />

          {currentCompletedSteps.length > 0 && (
            <AppColumn>
              {currentCompletedSteps.map((page, index) => {
                const isNextPage = nextPage && nextPage.id === page.id;
                const isCompleted = page.completed;

                const textColor =
                  isNextPage || isCompleted ? theme.colors.dark : theme.colors.grey;
                const IconColor = isCompleted
                  ? theme.colors.green
                  : isNextPage
                    ? theme.colors.dark
                    : theme.colors.grey;

                return (
                  <AppRow
                    c
                    rossAxisAlignment={"center"}
                    onClick={() => {
                      navigateToApplicationStep({
                        navigate,
                        page,
                        candidateData,
                        onClose,
                      });
                    }}
                    style={{
                      marginBottom: theme.spacing.elementPaddingSmall,
                      cursor: "pointer",
                    }}
                  >
                    <Icon
                      name={
                        page?.completed
                          ? "check circle"
                          : isNextPage
                            ? "dot circle outline"
                            : "circle outline"
                      }
                      style={{
                        color: IconColor,
                        fontSize: "1.2rem",
                      }}
                    />
                    <SizedBox width={theme.spacing.elementPaddingSmall} />
                    <AppLinkedText
                      text={page?.title}
                      small
                      color={textColor}
                      fontWeight={isNextPage || isCompleted ? 500 : 400}
                    />
                    <SizedBox width={theme.spacing.elementPaddingSmall} />

                    <Icon
                      name="external alternate"
                      size="small"
                      style={{
                        marginTop: "0.2rem",
                      }}
                    />
                  </AppRow>
                );
              })}
            </AppColumn>
          )}

          {unCompletedSteps.length > 0 && (
            <AppColumn>
              {unCompletedSteps.map((page, index) => {
                const isNextPage = nextPage && nextPage.id === page.id;
                const isCompleted = page.completed;

                const textColor =
                  isNextPage || isCompleted ? theme.colors.dark : theme.colors.grey;
                const IconColor = isCompleted
                  ? theme.colors.green
                  : isNextPage
                    ? theme.colors.dark
                    : theme.colors.grey;

                return (
                  <AppRow
                    crossAxisAlignment={"center"}
                    onClick={() => {
                      if (isNextPage) {
                        navigateToApplicationStep({
                          navigate,
                          page,
                          candidateData,
                          onClose,
                        });
                      }
                    }}
                    style={{
                      marginBottom: theme.spacing.elementPaddingSmall,
                      cursor: "pointer",
                    }}
                  >
                    <Icon
                      name={
                        page.completed
                          ? "check circle"
                          : isNextPage
                            ? "dot circle outline"
                            : "circle outline"
                      }
                      style={{
                        color: IconColor,
                        fontSize: "1.2rem",
                      }}
                    />
                    <SizedBox width={theme.spacing.elementPaddingSmall} />
                    <AppLinkedText
                      text={page.title}
                      small
                      cursor={isNextPage ? "pointer" : "default"}
                      color={textColor}
                      fontWeight={isNextPage || isCompleted ? 500 : 400}
                    />
                    <SizedBox width={theme.spacing.elementPaddingSmall} />
                    {isNextPage && (
                      <Icon
                        name="external alternate"
                        size="small"
                        style={{
                          marginTop: "0.2rem",
                        }}
                      />
                    )}
                  </AppRow>
                );
              })}
              <SizedBox height={theme.spacing.contentElementPadding} />

              {nextPage && <AppRow>
                <AppPrimaryButton
                  size="big"
                  onClick={() => {
                    navigateToApplicationStep({
                      navigate,
                      page: nextPage,
                      candidateData,
                      onClose,
                    });
                  }}
                  style={{
                    borderRadius: theme.radius.button,
                    minWidth: "180px",
                    width: isMobile && "100%",
                  }}
                >
                  {t("continue")}
                </AppPrimaryButton>
              </AppRow>}
            </AppColumn>
          )}
        </div>

        {userDocuments.length > 0 && (
          <AppColumn>
            <SizedBox height={theme.spacing.contentPadding} />
            <AppText.BodyText fontWeight={600}>
              {t("document_submitted")}
            </AppText.BodyText>
            <SizedBox height={theme.spacing.elementPadding} />
            {userDocuments.map((fileData) => {
              const nameType = getDocumentNameType(fileData.type);
              const isDeleting = isDeletedDocumentData[fileData.id] || false;

              return (
                <AppRow
                  crossAxisAlignment={"start"}
                  style={{
                    paddingBottom: theme.spacing.elementPadding,
                  }}
                >
                  <Icon
                    name="download"
                    style={{
                      marginTop: "0.3rem",
                    }}
                  />
                  <SizedBox width={theme.spacing.elementPaddingSmall} />
                  <AppColumn
                    style={{
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      downloadFile(fileData.fileLink, fileData.fileName);
                    }}
                  >
                    <AppText.Caption textDecoration="underline">
                      {fileData.fileName}
                    </AppText.Caption>

                    <SizedBox height={theme.spacing.elementPaddingSmall} />
                    <AppRow>
                      <AppText.FootnoteSmall
                        color={theme.colors.grey}
                      >{`${nameType}`}</AppText.FootnoteSmall>
                      <SizedBox width={theme.spacing.elementPaddingSmall} />
                      {fileData.createdAt && (
                        <AppText.FootnoteSmall
                          color={theme.colors.grey}
                        >{` | ${isoDate({
                          dateString: fileData.createdAt,
                        })}`}</AppText.FootnoteSmall>
                      )}
                    </AppRow>
                  </AppColumn>

                  {isDeleting ? (
                    <CircularLoader
                      style={{
                        height: "24px",
                        width: "24px",
                        marginLeft: isMobile ? "auto" : theme.spacing.elementPadding,
                      }}
                    />
                  ) : (
                    <Icon
                      name="trash alternate outline"
                      circular
                      style={{
                        height: "50px",
                        width: "50px",
                        marginLeft: isMobile ? "auto" : theme.spacing.elementPadding,
                        color: theme.colors.dark,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        onDeleteCandidateDocument(
                          fileData,
                          (newCandidateData) => {
                            if (onCandidateChange)
                              onCandidateChange(newCandidateData);

                            showToast(t("document_deleted"), {
                              type: "success",
                            });
                          },
                        );
                      }}
                    />
                  )}
                </AppRow>
              );
            })}
          </AppColumn>
        )}

        <SizedBox height={theme.spacing.contentElementPadding} />
        {eventDetailData && (
          <EventDetailSection eventDetailData={eventDetailData} />
        )}
        <SizedBox height={theme.spacing.contentElementPadding} />
      </AppColumn>
    </div>
  );
};

export default CandidateDetailMainContent;
